import { CircularProgress } from '@mui/material';
import MUIIconButton, { IconButtonProps as MUIIconButtonProps } from '@mui/material/IconButton';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { Icon, IconName, IconProps } from '../icon/index';

export type IconButtonProps = MUIIconButtonProps & {
	/**
	 * The name of the icon to be shown inside of the IconButton.
	 * @default undefined
	 */
	iconName?: IconName;
	/**
	 * When true, disables the button and renders a circular progress indicator around its circumference.
	 * @default undefined
	 */
	loading?: boolean;
	/**
	 * When true, centers the component on the cross-axis of its flex parent.
	 */
	alignCenter?: boolean;
	/**
	 * Specifies the color of the icon within the IconButton. If `iconColor` is not set,
	 * the icon will inherit the color specified by the IconButton's `color` property,
	 * which controls the color of the IconButton's ripple effect as well.
	 *
	 * @default 'inherit' - The icon color inherits from the IconButton's color.
	 */
	iconColor?: IconProps['color'];
	/**
	 * When true, provides visual feedback for clicks by showing a check icon for 2 seconds.
	 */
	confirmWithCheck?: boolean;
};

const AnimationWrapper = ({ children }) => {
	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
			{children}
		</motion.div>
	);
};

/**
 * This extension of the MUI IconButton adds some common functionality such as a `loading` state, and makes styling easier by allowing you to pass an `iconName`.
 * It also addresses a common styling gotcha that results in stretched IconButtons.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
	const { iconName, iconColor, loading, alignCenter, size, children, onClick, confirmWithCheck, ...buttonProps } = props;
	const color = iconColor ?? 'inherit';
	const [showCheck, setShowCheck] = useState(false);

	const handleCheck = () => {
		setShowCheck(true);
		setTimeout(() => {
			setShowCheck(false);
		}, 2000);
	};

	return (
		<MUIIconButton
			style={{ alignSelf: alignCenter ? 'center' : undefined }}
			disabled={loading}
			type="button"
			onClick={e => {
				onClick?.(e);
				if (confirmWithCheck) {
					handleCheck();
				}
			}}
			{...buttonProps}
			ref={ref}
		>
			<AnimatePresence>
				{loading ? (
					<AnimationWrapper>
						<CircularProgress style={{ position: 'absolute', width: '100%', height: '100%', inset: 0 }} />
					</AnimationWrapper>
				) : null}
			</AnimatePresence>
			{iconName ? (
				<Icon fontSize={size} iconName={showCheck ? 'Check' : iconName} color={buttonProps.disabled || loading ? 'inherit' : color} />
			) : (
				children
			)}
		</MUIIconButton>
	);
});
